import { useState } from 'react';
import { DialogActions, Divider } from '@mui/material';
import DialogBox from '../../../core-components/theme/components/DialogBox/DialogBox';
import SaveCancelButtons from '../../../core-components/theme/components/SaveCancelComponent/SaveCancelButtons';
import { useDataLoggerDetails, DataLoggerDetails } from '../hooks/useDataLoggerDetails';
import { useCreateOrUpdateDataLogger, SensorConfig } from '../hooks/useCreateOrUpdateDataLogger';
import ConfigurationDialogTabs from './ConfigurationDialogTabs';
import { isFirstTabComplete, mapSensorConfigsFromDetails, getSaveCancelButtonsProps } from '../helpers/dialogHelper';
import Loader from '../../../globalComponents/loader/DPLoader';
import { validateFirstTab, validateSensorConfigFrontend } from '../helpers/DataLoggerConfigurationDialogHelper';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';

function removeEmptyStrings(obj: any): any {
  if (Array.isArray(obj)) {
    return obj
      .map(removeEmptyStrings)
      .filter(item => item !== undefined && item !== null && item !== '');
  } else if (typeof obj === 'object' && obj !== null) {
    return Object.entries(obj).reduce((acc, [key, value]) => {
      if (
        value !== '' &&
        value !== null &&
        value !== undefined
      ) {
        acc[key] = removeEmptyStrings(value);
      }
      return acc;
    }, {} as any);
  }
  return obj;
}

interface DataLoggerConfigurationDialogProps {
  open: boolean;
  onClose: () => void;
  initialData?: {
    serialNumber?: string;
    id?: string;
  };
}

export default function DataLoggerConfigurationDialog({
  open,
  onClose,
  initialData,
}: DataLoggerConfigurationDialogProps) {
  const [activeTab, setActiveTab] = useState(0);
  const queryClient = useQueryClient();
  const { data: dataLoggerDetails, isLoading } = useDataLoggerDetails(initialData?.id);

  const { mutate: handleSubmit, isPending } = useCreateOrUpdateDataLogger();
  const [formData, setFormData] = useState<Partial<DataLoggerDetails>>({
    serialNumber: initialData?.serialNumber || '',
    name: '',
    debug: false,
    plcBrand: 'Fuji',
    pollingInterval: 60,
    plcModel: 'NA0PB60R-34C',
    asset: { _id: '', plantName: '' },
    modbusHost: '',
    modbusPort: 0,
    unitIdReal: 0,
    startAddressReal: 0,
    registerCountReal: 0,
    unitIdBool: 0,
    startAddressBool: 0,
    registerCountBool: 0,
    sensorConfig: [],
  });

  const [sensorConfigs, setSensorConfigs] = useState<{ [key: string]: SensorConfig }>({});

  if (dataLoggerDetails && dataLoggerDetails._id) {
    if (!formData._id || formData._id !== dataLoggerDetails._id) {
      setFormData(dataLoggerDetails);
      setSensorConfigs(mapSensorConfigsFromDetails(dataLoggerDetails));
    }
  }
  
  const [expandedSensors, setExpandedSensors] = useState<string[]>([]);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [sensorConfigErrors, setSensorConfigErrors] = useState<{ [sensorId: string]: { [field: string]: string } }>({});

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleDialogClose = () => {
    setActiveTab(0);
    setErrors({});
    setSensorConfigErrors({});
    setExpandedSensors([]);
    setFormData({
      serialNumber: initialData?.serialNumber || '',
      name: '',
      debug: false,
      plcBrand: 'Fuji',
      pollingInterval: 60,
      plcModel: 'NA0PB60R-34C',
      asset: { _id: '', plantName: '' },
      modbusHost: '',
      modbusPort: 0,
      unitIdReal: 0,
      startAddressReal: 0,
      registerCountReal: 0,
      unitIdBool: 0,
      startAddressBool: 0,
      registerCountBool: 0,
      sensorConfig: [],
    });
    setSensorConfigs({});
    onClose();
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => {
      let updated = { ...prev, [field]: value };
      if (field === 'plcBrand') {
        updated.plcModel = value === 'Fuji' ? 'NA0PB60R-34C' : value === 'Delta' ? 'DVP SE' : value === 'Siemens' ? 'S7-1200' : '';
      }
      return updated;
    });
  };

  const handleAssetChange = (asset: any) => {
    setFormData(prev => ({
      ...prev,
      asset: asset
        ? { _id: asset._id, plantName: asset.plantName }
        : { _id: '', plantName: '' },
    }));
    setSensorConfigs({});
    setExpandedSensors([]);
  };

  const handleSensorConfigChange = (sensorId: string, field: string, value: any) => {
    setSensorConfigs(prev => {
      const prevConfig = prev[sensorId] || {};
      let updatedConfig = {
        ...prevConfig,
        [field]: value,
      };
      if (!prevConfig.isNewEntry && !prevConfig.isDeleted) {
        updatedConfig.isModified = true;
      }
      return {
        ...prev,
        [sensorId]: updatedConfig,
      };
    });
    setSensorConfigErrors(prev => {
      if (!prev[sensorId]) return prev;
      const { [field]: removed, ...rest } = prev[sensorId];
      return { ...prev, [sensorId]: rest };
    });
  };

  const handleSensorToggle = (sensorId: string) => {
    setExpandedSensors(prev =>
      prev.includes(sensorId)
        ? prev.filter(id => id !== sensorId)
        : [sensorId]
    );
  };

  const handleSensorSelect = (selectedSensors: any) => {
    const selectedIds = Array.isArray(selectedSensors)
      ? new Set(selectedSensors.map(s => s._id))
      : selectedSensors && selectedSensors._id
        ? new Set([selectedSensors._id])
        : new Set();

    setSensorConfigs(prev => {
      const newConfigs = { ...prev };
      selectedIds.forEach(id => {
        if (newConfigs[id]) {
          newConfigs[id] = { ...newConfigs[id], isDeleted: false };
        }
      });
      if (Array.isArray(selectedSensors)) {
        selectedSensors.forEach(sensor => {
          if (!newConfigs[sensor._id]) {
            newConfigs[sensor._id] = {
              sensor: { _id: sensor._id, name: sensor.name, sensorTag: sensor.sensorTag },
              sensorType: sensor.sensorType || sensor.type || 'boolean',

              wordOrder: 'CDAB',
              isNewEntry: true,
              modbusMisfireTotalizer: false,
              type: 'float',
              scalingFactor: 1,
            };
          }
        });
      } else if (selectedSensors && selectedSensors._id && !newConfigs[selectedSensors._id]) {
        newConfigs[selectedSensors._id] = {
          sensor: { _id: selectedSensors._id, name: selectedSensors.name, sensorTag: selectedSensors.sensorTag || '' },
          sensorTag: selectedSensors.sensorTag || '',
          sensorType: selectedSensors.sensorType || selectedSensors.type || 'boolean',
          isNewEntry: true,
          modbusMisfireTotalizer: false,
          scalingFactor: 1,
        };
      }
      return newConfigs;
    });

    if (Array.isArray(selectedSensors)) {
      setExpandedSensors(prev => {
        const prevIds = prev;
        const newIds = selectedSensors.map(s => s._id);
        const added = newIds.filter(id => !prevIds.includes(id));
        if (added.length > 0) return [added[added.length - 1]];
        return prev;
      });
    } else if (selectedSensors && selectedSensors._id) {
      setExpandedSensors([selectedSensors._id]);
    } else {
      setExpandedSensors([]);
    }
  };

  const handleSensorRemove = (sensorId: string) => {
    const isReferenced = Object.values(sensorConfigs).some(config => 
      !config.isDeleted &&
      config.referenceSensor &&
      typeof config.referenceSensor === 'object' &&
      config.referenceSensor._id === sensorId
    );

    if (isReferenced) {
      toast.error("This sensor is being used as a reference sensor in another sensor and cannot be deleted.");
      return;
    }

    setSensorConfigs(prev => {
      const newConfigs = { ...prev };
      if (newConfigs[sensorId]?.isNewEntry) {
        delete newConfigs[sensorId];
      } else {
        newConfigs[sensorId] = {
          ...newConfigs[sensorId],
          isDeleted: true,
        };
      }
      return newConfigs;
    });
    setExpandedSensors(prev => prev.filter(id => id !== sensorId));
  };

  const handleNext = () => {
    if (validateFirstTab(formData, setErrors)) {
      setActiveTab(1);
    }
  };

  const handleBack = () => {
    setActiveTab(0);
  };

  const propagateIsModifiedToReferenceSensors = (sensorConfigs: { [key: string]: any }) => {
    const modifiedSensorIds = new Set<string>();
    Object.values(sensorConfigs).forEach(config => {
      if (config.isModified) {
        if (config.sensor?._id) {
          modifiedSensorIds.add(config.sensor._id);
        } else if (config._id) {
          modifiedSensorIds.add(config._id);
        }
      }
    });

    const updatedConfigs: { [key: string]: any } = { ...sensorConfigs };
    let changed = false;
    Object.entries(sensorConfigs).forEach(([key, config]) => {
      const refId = config.referenceSensor?._id || config.referenceSensor;
      if (refId && modifiedSensorIds.has(refId) && !config.isModified) {
        updatedConfigs[key] = { ...config, isModified: true };
        changed = true;
      }
    });

    if (changed) {
      return propagateIsModifiedToReferenceSensors(updatedConfigs);
    }
    return updatedConfigs;
  };



  const handleSubmitForm = () => {
    let updatedSensorConfigs = propagateIsModifiedToReferenceSensors(sensorConfigs);

    const activeSensorConfigs = Object.values(updatedSensorConfigs).filter(config => !config.isDeleted);

    if (activeSensorConfigs.length === 0) {
      toast.error("At least one sensor must be configured.");
      setActiveTab(1);
      return;
    }

    const { _id, createdAt, createdBy, version, updatedAt, assetId, __v, isDeleted, ...finalFormData } = formData;
    const sensorConfigArray = Object.values(updatedSensorConfigs).map(config => {
      if (config.sensorType === 'analog' && config.referenceSensor && config.referenceSensorPurpose === 'totalizer') {
        const { start, end, type, wordOrder, scalingFactor, function: func, modbusMisfireTotalizer, ...rest } = config;
        return rest;
      }
      if (config.referenceSensor == null) {
        const { referenceSensor, ...rest } = config;
        return rest;
      }
      return config;
    });


    const errors: { [sensorId: string]: { [field: string]: string } } = {};
    sensorConfigArray.forEach(config => {
      const configErrors = validateSensorConfigFrontend(config);
      if (Object.keys(configErrors).length > 0) {
        errors[config.sensor._id] = configErrors;
      }
    });

    setSensorConfigErrors(errors);
    if (Object.keys(errors).length > 0) {
      setExpandedSensors(Object.keys(errors));
      setActiveTab(1);
      return;
    }

    const payload = {
      ...finalFormData,
      dataLoggerId: initialData?.id,
      sensorConfig: sensorConfigArray,
    } as any;

    const cleanedPayload = removeEmptyStrings(payload);
    handleSubmit(cleanedPayload, {
      onSuccess: () => {
        if (initialData?.id) {
          queryClient.invalidateQueries({ queryKey: ['dataLoggerDetails', initialData.id] });
        }
        queryClient.invalidateQueries({ queryKey: ['dataLoggerTable'] });
        onClose();
      },
    });
  };

  const saveCancelProps = getSaveCancelButtonsProps(
    activeTab,
    isFirstTabComplete(formData),
    isPending,
    handleNext,
    handleBack,
    handleSubmitForm,
    handleDialogClose,
  );


  return (
    <DialogBox
      open={open}
      handleClose={handleDialogClose}
      maxWidth="lg"
      minWidth="50%"
      height="80%"
      title="Data Logger Configuration"
      padding="10px 20px 0px 20px"
    >
      {isLoading ? <Loader /> :
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <ConfigurationDialogTabs
              activeTab={activeTab}
              formData={formData}
              sensorConfigs={sensorConfigs}
              expandedSensors={expandedSensors}
              isFirstTabComplete={isFirstTabComplete(formData)}
              onTabChange={handleTabChange}
              onInputChange={handleInputChange}
              onAssetChange={handleAssetChange}
              onSensorConfigChange={handleSensorConfigChange}
              onSensorToggle={handleSensorToggle}
              onSensorSelect={handleSensorSelect}
              onSensorRemove={handleSensorRemove}
              initialData={initialData}
              onNext={handleNext}
              errors={errors}
              setErrors={setErrors}
              sensorConfigErrors={sensorConfigErrors}
            />

          </div>
          <DialogActions sx={{ display: 'flex', justifyContent: 'flex-end', position: 'sticky', bottom: 0, background: '#fff', zIndex: 2, borderTop: '1px solid #eee' }}>
            <SaveCancelButtons {...saveCancelProps} containerStyle={{ marginTop: '10px' }} />
          </DialogActions>
        </div>
      }
    </DialogBox>
  );
} 